import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function TechArchitectureSection() {
  const { tbcspnLayers, techStack } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeLayer, setActiveLayer] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => {
      setActiveLayer((p) => (p + 1) % tbcspnLayers.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [visible, tbcspnLayers.length]);

  const layerColors: Record<string, { badge: string; border: string; bg: string; dot: string; agentBg: string; agentText: string }> = {
    amber: { badge: "text-amber-600 bg-amber-50 border-amber-200", border: "border-amber-200", bg: "bg-amber-50/60", dot: "bg-amber-500", agentBg: "bg-amber-100", agentText: "text-amber-700" },
    stone: { badge: "text-stone-600 bg-stone-100 border-stone-200", border: "border-stone-200", bg: "bg-stone-50", dot: "bg-stone-500", agentBg: "bg-stone-200", agentText: "text-stone-700" },
    emerald: { badge: "text-emerald-600 bg-emerald-50 border-emerald-200", border: "border-emerald-200", bg: "bg-emerald-50/60", dot: "bg-emerald-500", agentBg: "bg-emerald-100", agentText: "text-emerald-700" },
  };

  const memoryItems = [
    { icon: "ri-database-2-line", title: t("tech_mem1_title"), desc: t("tech_mem1_desc") },
    { icon: "ri-history-line", title: t("tech_mem2_title"), desc: t("tech_mem2_desc") },
    { icon: "ri-refresh-line", title: t("tech_mem3_title"), desc: t("tech_mem3_desc") },
  ];

  return (
    <section ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("tech_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("tech_title_1")}
            <br />
            <span className="text-amber-500">{t("tech_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("tech_subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          <div
            className={`space-y-4 transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            {tbcspnLayers.map((layer, i) => {
              const c = layerColors[layer.color];
              const isActive = activeLayer === i;
              return (
                <button
                  key={layer.id}
                  onClick={() => setActiveLayer(i)}
                  className={`w-full text-left rounded-2xl border p-6 transition-all duration-400 cursor-pointer ${
                    isActive ? `${c.border} ${c.bg}` : "border-stone-100 bg-white hover:border-stone-200"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl border transition-all duration-300 ${isActive ? c.badge : "bg-stone-50 border-stone-100 text-stone-400"}`}>
                      <i className={`${layer.icon} text-xl`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-stone-900 text-sm">{layer.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${isActive ? c.badge : "bg-stone-50 border-stone-200 text-stone-400"}`}>
                          {layer.sublabel}
                        </span>
                      </div>
                      <p className={`text-xs font-semibold mb-2 ${isActive ? "text-stone-600" : "text-stone-400"}`}>
                        {layer.role}
                      </p>
                      <p className={`text-sm leading-relaxed transition-all duration-300 ${isActive ? "text-stone-600 max-h-20 opacity-100" : "text-stone-400 max-h-0 opacity-0 overflow-hidden"}`}>
                        {layer.description}
                      </p>
                      {isActive && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {layer.agents.map((agent) => (
                            <span key={agent} className={`text-xs px-2.5 py-1 rounded-full font-medium ${c.agentBg} ${c.agentText}`}>
                              {agent}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 transition-all ${isActive ? `${c.dot} scale-125` : "bg-stone-200"}`} />
                  </div>
                </button>
              );
            })}

            <div className="p-5 bg-stone-900 rounded-2xl flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/20 flex-shrink-0">
                <i className="ri-plug-2-line text-amber-400 text-lg" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-1">{t("tech_mcp_title")}</p>
                <p className="text-stone-400 text-xs leading-relaxed">{t("tech_mcp_desc")}</p>
              </div>
            </div>
          </div>

          <div
            className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            <div
              className="rounded-3xl overflow-hidden border border-stone-100"
              style={{
                backgroundImage: `url(https://public.readdy.ai/ai/img_res/c8d977575161f4339f63ab11bc781f32.jpg)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="bg-stone-900/80 backdrop-blur-sm p-8 h-full">
                <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-6">{t("tech_memory_label")}</p>
                <div className="space-y-4">
                  {memoryItems.map((item) => (
                    <div key={item.title} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-400/15 flex-shrink-0">
                        <i className={`${item.icon} text-amber-400 text-base`} />
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold mb-1">{item.title}</p>
                        <p className="text-stone-400 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-center text-stone-400 text-xs font-bold uppercase tracking-widest mb-8">{t("tech_stack_label")}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {techStack.map((stack, i) => (
              <div
                key={stack.category}
                className={`p-6 rounded-2xl border border-stone-100 bg-stone-50 hover:-translate-y-1 transition-all duration-300 cursor-default ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: `${400 + i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-stone-100 mb-4">
                  <i className={`${stack.icon} text-stone-600 text-lg`} />
                </div>
                <p className="font-bold text-stone-800 text-sm mb-3">{stack.category}</p>
                <ul className="space-y-1.5">
                  {stack.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-fill text-amber-400 text-xs mt-1 flex-shrink-0" />
                      <span className="text-stone-500 text-xs leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
